import { useState } from "react";
import { RefreshCw, CalendarClock, Mail, Copy, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "—");

const DigestEditor = ({ guideId, h, onDone }) => {
  const [f, setF] = useState({ headline: h.digest?.headline || "", buyer_note: h.digest?.buyer_note || "" });
  const save = async () => {
    try {
      await api.put(`/guides/${guideId}/editions/${h.edition}/digest`, f);
      toast.success("Buyer note saved — used in buyer emails and the public changelog");
      onDone(true);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`digest-editor-${h.edition}`} className="bg-[var(--g-mist,#EFE6F3)] border-l-4 border-[var(--g-accent,#C59B27)] p-4 space-y-2">
      <Input data-testid={`digest-headline-input-${h.edition}`} value={f.headline} onChange={(e) => setF({ ...f, headline: e.target.value })} placeholder="Headline for buyers" className="bg-white rounded-none h-9 text-sm" />
      <Textarea data-testid={`digest-note-input-${h.edition}`} rows={6} value={f.buyer_note} onChange={(e) => setF({ ...f, buyer_note: e.target.value })} placeholder="What is new in this edition (Markdown)" className="bg-white rounded-none text-xs" />
      <div className="flex gap-2">
        <Button size="sm" data-testid={`digest-save-${h.edition}`} onClick={save} disabled={!f.headline || !f.buyer_note} className="rounded-none h-8">Save note</Button>
        <Button size="sm" variant="ghost" data-testid={`digest-cancel-${h.edition}`} onClick={() => onDone(false)} className="rounded-none h-8">Cancel</Button>
      </div>
    </div>
  );
};

const History = ({ guideId, items, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(null);
  return (
    <ol className="space-y-3" data-testid="update-history">
      {[...items].reverse().map((h) => (
        <li key={h.edition + h.date} className="border-l-2 border-[var(--g-accent,#C59B27)] pl-4">
          <button data-testid={`history-edition-${h.edition}`} onClick={() => setOpen(open === h.edition ? null : h.edition)} className="text-left">
            <span className="font-display text-[var(--g-dark,#3B1B48)]">Edition {h.edition}</span>
            <span className="text-xs text-[#8C827A] ml-2">{fmt(h.date)} · {h.changes.length} note{h.changes.length === 1 ? "" : "s"}</span>
          </button>
          {open === h.edition && (
            <div className="mt-2 space-y-3">
              {editing === h.edition ? <DigestEditor guideId={guideId} h={h} onDone={(saved) => { setEditing(null); if (saved) onChanged(); }} /> : h.digest ? (
                <div data-testid={`history-digest-${h.edition}`} className="bg-[var(--g-mist,#EFE6F3)] border-l-4 border-[var(--g-accent,#C59B27)] p-4">
                  <div className="flex items-center justify-between mb-1">
                    <p className="eyebrow">Ready for past buyers</p>
                    <button data-testid={`copy-digest-${h.edition}`} onClick={() => navigator.clipboard.writeText(`${h.digest.headline}\n\n${h.digest.buyer_note}`).then(() => toast.success("Copied"))} className="text-[var(--g-dark,#3B1B48)] hover:text-[var(--g-accent,#C59B27)]"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                  <p className="font-display text-[var(--g-dark,#3B1B48)] mb-1">{h.digest.headline}</p>
                  <p className="text-xs whitespace-pre-line text-[#1C181D]">{h.digest.buyer_note}</p>
                  <button data-testid={`edit-digest-${h.edition}`} onClick={() => setEditing(h.edition)} className="mt-2 text-xs text-[var(--g-dark,#3B1B48)] hover:text-[var(--g-accent,#C59B27)] flex items-center gap-1"><Pencil className="w-3 h-3" />Edit wording</button>
                </div>
              ) : (
                <button data-testid={`add-digest-${h.edition}`} onClick={() => setEditing(h.edition)} className="text-xs text-[var(--g-dark,#3B1B48)] hover:text-[var(--g-accent,#C59B27)] flex items-center gap-1"><Pencil className="w-3 h-3" />Write a buyer note for this edition</button>
              )}
              <ul className="space-y-1 text-xs text-[#1C181D]">{h.changes.map((c, i) => <li key={i}>— {c}</li>)}</ul>
              {h.buyers_notified != null && <p data-testid={`history-notified-${h.edition}`} className="text-xs text-[#2D6A4F]">Emailed to {h.buyers_notified} past buyer{h.buyers_notified === 1 ? "" : "s"} · {new Date(h.buyers_notified_at).toLocaleDateString("en-AU")}</p>}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
};

const ReminderRow = ({ guide }) => {
  const [busy, setBusy] = useState(false);
  const last = [...(guide.reminders || [])].reverse().find((r) => !r.test);
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/guides/${guide.id}/reminders/test`);
      toast.success(`Test reminder sent to ${data.to}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const notify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/guides/${guide.id}/notify-buyers`);
      toast.success(`Edition ${guide.edition} emailed to ${data.sent} past buyer${data.sent === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3 mt-5 pt-5 border-t border-[#E2D9CC] text-xs text-[#8C827A]">
      <Mail className="w-4 h-4 text-[var(--g-accent,#C59B27)]" strokeWidth={1.4} />
      <span data-testid="reminder-status" className="flex-1">
        {last ? `Reminder sent ${fmt(last.at)} (${last.stage === "due" ? "due date" : "2 weeks before"})` : "Email reminders: 2 weeks before and on the due date"}
      </span>
      <button data-testid="send-test-reminder-button" onClick={test} disabled={busy} className="text-[var(--g-dark,#3B1B48)] hover:text-[var(--g-accent,#C59B27)] underline-offset-2 hover:underline transition-colors">
        {busy ? "Sending…" : "Send test reminder"}
      </button>
      <button data-testid="notify-buyers-button" onClick={notify} disabled={busy} className="text-[var(--g-dark,#3B1B48)] hover:text-[var(--g-accent,#C59B27)] underline-offset-2 hover:underline transition-colors">
        Email past buyers
      </button>
    </div>
  );
};

export const UpdatePanel = ({ guide, onUpdate, onChanged }) => (
  <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-px bg-[#E2D9CC] border border-[#E2D9CC] mb-10" data-testid="update-panel">
    <div className="bg-[#F4EFE6] p-7">
      <div className="flex items-center gap-3 mb-5">
        <CalendarClock className="w-5 h-5 text-[var(--g-accent,#C59B27)]" strokeWidth={1.4} />
        <p className="eyebrow">Accuracy · 6-month update cycle</p>
        <span data-testid="update-due-badge" className={`ml-auto px-2.5 py-1 text-[11px] font-mono-accent uppercase tracking-wider border ${guide.update_due ? "bg-[#B45309]/10 text-[#B45309] border-[#B45309]/30" : "bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/30"}`}>
          {guide.update_due ? "Update due" : "Up to date"}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-6 mb-6">
        <div><p className="text-xs text-[#8C827A]">Edition</p><p data-testid="guide-edition" className="font-display text-3xl text-[var(--g-dark,#3B1B48)]">{guide.edition}</p></div>
        <div><p className="text-xs text-[#8C827A]">Last updated</p><p data-testid="guide-last-updated" className="text-sm text-[#1C181D] mt-2">{fmt(guide.last_refreshed_at || guide.created_at)}</p></div>
        <div><p className="text-xs text-[#8C827A]">Next update due</p><p data-testid="guide-next-update" className={`text-sm mt-2 ${guide.update_due ? "text-[#B45309] font-medium" : "text-[#1C181D]"}`}>{fmt(guide.next_update_due)}</p></div>
      </div>
      {guide.update_error && <p data-testid="update-error" className="text-xs text-red-700 mb-4">{guide.update_error}</p>}
      <Button data-testid="update-guide-button" onClick={onUpdate} className="rounded-none h-11">
        <RefreshCw className="w-4 h-4 mr-2" />Update guide now
      </Button>
      <p className="text-xs text-[#8C827A] mt-3">Re-researches every source, rewrites all chapters, re-typesets the PDF and toolkit and logs what changed. Your store listing, issue number and publish status are kept; the current edition stays downloadable until the new one is ready.</p>
      <ReminderRow guide={guide} />
    </div>
    <div className="bg-[#FAF7F2] p-7">
      <p className="eyebrow mb-4">Edition history</p>
      <History guideId={guide.id} items={guide.refresh_history || []} onChanged={onChanged} />
    </div>
  </div>
);
